/**
 * vitaboom.service.ts
 *
 * Broker access to the Vitaboom supplement-protocol embed. The browser SDK talks
 * to Vitaboom directly with a short-lived (15-minute) JWT; that JWT is minted by
 * our Laravel backend from a server-only partner API key. The frontend never
 * sees the partner key — it only ever calls our own `/vitaboom/token` route.
 */
import type { AxiosInstance } from 'axios';
import { apiClient } from './apiClient';

export interface VitaboomToken {
  token: string;
  expiresIn: string;
  /** Echoed pilot clinic id (self-check that we're scoped to the right clinic). */
  clinicId: string;
}

/**
 * Mint (or re-mint) a Vitaboom JWT for the current staff user. Safe to call
 * repeatedly — the SDK's onUnauthorized handler calls this again whenever a
 * token expires mid-session.
 */
export async function getVitaboomToken(): Promise<VitaboomToken> {
  const res = await apiClient.post<{ data: VitaboomToken }>('/vitaboom/token');
  return res.data.data;
}

/**
 * Whether the Vitamins (Vitaboom) module is enabled for the current account.
 * Opt-in / default-OFF — driven by the same modules/aa_accounts_modules gate as
 * the other integrations, toggled in Prospera-Corp-App. Gates the Vitamins tab.
 */
export async function getVitaboomEnabled(): Promise<boolean> {
  const res = await apiClient.get<{ data: { enabled: boolean } }>('/vitaboom/status');
  return res.data.data.enabled;
}

/** Vitaboom's own patient record (GUID-keyed, clinic-scoped). */
interface VitaboomPatientRecord {
  id: string;
  email?: string | null;
  /** Free-text external-record slot — we store our numeric FTP patientID here. */
  medicalRecordId?: string | null;
}

interface VitaboomEnvelope<T> {
  success: boolean;
  data: T;
}

/**
 * Resolve (or create) the Vitaboom patient GUID for one of our patients.
 *
 * Vitaboom's share/order endpoints validate `patientId` as a GUID referencing a
 * row in THEIR patients table — our numeric patientID is rejected outright
 * ("patientId must be a valid GUID"). The mapping key is Vitaboom's
 * `medicalRecordId` field, where we store the FTP patientID as a string.
 *
 * Runs against the Vitaboom axios client (partner JWT), not our apiClient.
 * Their `?search=` param does not filter (verified on staging), so we list and
 * match locally: medicalRecordId first, then email as a fallback for patients
 * created before this mapping existed (their record is adopted as-is).
 */
export async function ensureVitaboomPatient(
  vitaboomApi: AxiosInstance,
  ftpPatientId: number,
  demographics: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  },
): Promise<string> {
  const mrn = String(ftpPatientId);

  const list = await vitaboomApi.get<VitaboomEnvelope<VitaboomPatientRecord[]>>('/patients');
  const rows = Array.isArray(list.data?.data) ? list.data.data : [];

  const byMrn = rows.find((p) => p.medicalRecordId === mrn);
  if (byMrn) return byMrn.id;

  const email = demographics.email?.trim().toLowerCase();
  if (email) {
    const byEmail = rows.find((p) => p.email?.trim().toLowerCase() === email);
    if (byEmail) return byEmail.id;
  }

  // Vitaboom requires a valid email (their validator refuses reserved
  // domains such as example.test and malformed addresses from old charts).
  // Without one, say so plainly instead of attempting a registration that is
  // bound to fail and reads as "couldn't connect".
  if (!email) throw new VitaboomPatientError(null, 'email');

  let created;
  try {
    created = await vitaboomApi.post<VitaboomEnvelope<VitaboomPatientRecord>>('/patients', {
      firstName: demographics.firstName ?? 'Patient',
      lastName: demographics.lastName ?? mrn,
      email: demographics.email,
      ...(demographics.phone ? { phone: demographics.phone } : {}),
      medicalRecordId: mrn,
    });
  } catch (e) {
    // A validation refusal is about this patient's chart, not the connection.
    if (vendorStatus(e) === 400 || vendorStatus(e) === 422) {
      throw new VitaboomPatientError(vendorMessage(e), rejectedField(e));
    }
    throw e;
  }
  const id = created.data?.data?.id;
  if (!id) throw new Error('Vitaboom returned no patient id');
  return id;
}

/**
 * Vitaboom refused to register the patient (a validation error on their side),
 * as opposed to Vitaboom being unreachable. The tab says which, so staff fix
 * the chart instead of retrying a connection that is fine.
 */
export class VitaboomPatientError extends Error {
  constructor(
    public readonly vendorMessage: string | null,
    /** The chart field Vitaboom refused (`email`, `phone`, …), when known. */
    public readonly field: string | null = null,
  ) {
    super(vendorMessage ?? 'Vitaboom refused this patient.');
    this.name = 'VitaboomPatientError';
  }
}

function vendorStatus(e: unknown): number | undefined {
  return (e as { response?: { status?: number } })?.response?.status;
}

function vendorMessage(e: unknown): string | null {
  const msg = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof msg === 'string' && msg.trim() !== '' ? msg.trim() : null;
}

/** The field a 400 names ("Validation error: email: …"), when it names one. */
function rejectedField(e: unknown): string | null {
  if (vendorStatus(e) !== 400) return null;
  const m = vendorMessage(e)?.match(/Validation error:\s*([A-Za-z_]+)\s*:/);
  return m ? m[1].toLowerCase() : null;
}
